export function widgetStyles(primary: string): string {
  const p = /^#[0-9A-Fa-f]{6}$/.test(primary) ? primary : '#0F766E';
  return `
:host {
  all: initial;
  font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
  color: #0f172a;
  --mia-primary: ${p};
  --mia-bg: #ffffff;
  --mia-muted: #64748b;
  --mia-border: #e2e8f0;
  --mia-user-bg: #ecfeff;
  --mia-bot-bg: #f8fafc;
  --mia-danger: #b91c1c;
  --mia-radius: 16px;
  --mia-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
  --mia-safe-bottom: env(safe-area-inset-bottom, 0px);
  --mia-safe-right: env(safe-area-inset-right, 0px);
  --mia-safe-left: env(safe-area-inset-left, 0px);
}

*, *::before, *::after { box-sizing: border-box; }

.mia-root {
  position: fixed;
  z-index: 2147483000;
  inset: auto;
  pointer-events: none;
}

.mia-root.pos-right { right: calc(16px + var(--mia-safe-right)); bottom: calc(16px + var(--mia-safe-bottom)); }
.mia-root.pos-left { left: calc(16px + var(--mia-safe-left)); bottom: calc(16px + var(--mia-safe-bottom)); }

.mia-fab {
  pointer-events: auto;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: var(--mia-primary);
  color: #fff;
  cursor: pointer;
  box-shadow: var(--mia-shadow);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  line-height: 1;
}

.mia-fab:focus-visible {
  outline: 3px solid #fff;
  outline-offset: 2px;
  box-shadow: 0 0 0 5px var(--mia-primary);
}

.mia-panel {
  pointer-events: auto;
  position: absolute;
  bottom: 72px;
  width: min(380px, calc(100vw - 24px));
  height: min(560px, calc(100vh - 96px - var(--mia-safe-bottom)));
  background: var(--mia-bg);
  border: 1px solid var(--mia-border);
  border-radius: var(--mia-radius);
  box-shadow: var(--mia-shadow);
  display: none;
  flex-direction: column;
  overflow: hidden;
}

.mia-root.pos-right .mia-panel { right: 0; }
.mia-root.pos-left .mia-panel { left: 0; }

.mia-panel.open { display: flex; }

.mia-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--mia-primary);
  color: #fff;
}

.mia-logo {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
  background: rgba(255,255,255,0.2);
}

.mia-title {
  flex: 1;
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}

.mia-icon-btn {
  border: none;
  background: transparent;
  color: inherit;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
}

.mia-icon-btn:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 1px;
}

.mia-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f1f5f9;
}

.mia-msg {
  max-width: 88%;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.mia-msg.user {
  align-self: flex-end;
  background: var(--mia-user-bg);
  border: 1px solid #a5f3fc;
}

.mia-msg.assistant, .mia-msg.system {
  align-self: flex-start;
  background: var(--mia-bot-bg);
  border: 1px solid var(--mia-border);
}

.mia-status {
  padding: 0 12px 8px;
  font-size: 12px;
  color: var(--mia-muted);
  min-height: 18px;
}

.mia-status.error { color: var(--mia-danger); }

.mia-composer {
  display: flex;
  gap: 8px;
  padding: 10px 12px calc(10px + var(--mia-safe-bottom));
  border-top: 1px solid var(--mia-border);
  background: #fff;
}

.mia-input {
  flex: 1;
  border: 1px solid var(--mia-border);
  border-radius: 10px;
  padding: 10px 12px;
  font: inherit;
  font-size: 14px;
  resize: none;
  min-height: 42px;
  max-height: 120px;
}

.mia-input:focus-visible {
  outline: 2px solid var(--mia-primary);
  outline-offset: 1px;
}

.mia-send {
  border: none;
  border-radius: 10px;
  background: var(--mia-primary);
  color: #fff;
  padding: 0 14px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.mia-send:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.mia-send:focus-visible {
  outline: 2px solid #0f172a;
  outline-offset: 2px;
}

.mia-fallback {
  pointer-events: auto;
  max-width: min(320px, calc(100vw - 32px));
  background: #fff;
  border: 1px solid var(--mia-border);
  border-radius: 12px;
  box-shadow: var(--mia-shadow);
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.4;
  color: #0f172a;
}

.mia-fallback p { margin: 0 0 10px; }
.mia-fallback .mia-send { width: 100%; height: 36px; }

.mia-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}

@media (max-width: 480px) {
  .mia-root.pos-right, .mia-root.pos-left {
    right: 0;
    left: 0;
    bottom: 0;
  }
  .mia-fab {
    position: absolute;
    right: calc(16px + var(--mia-safe-right));
    bottom: calc(16px + var(--mia-safe-bottom));
  }
  .mia-root.pos-left .mia-fab {
    right: auto;
    left: calc(16px + var(--mia-safe-left));
  }
  .mia-panel {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
    bottom: 0;
  }
  .mia-root.pos-right .mia-panel,
  .mia-root.pos-left .mia-panel {
    right: 0;
    left: 0;
  }
}
`;
}
